import { Stack } from "@mui/material";
import type { FC } from "react";
import { usePortfolioDocumentsQuery } from "web_myplann/Api/Portfolio/usePortfolioDocumentsQuery";
import type { PropertyInsuranceDetailDataDTO } from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { DetailSectionLoader } from "web_myplann/Portfolio/Components/DetailSectionLoader";
import { BasicInfo } from "web_myplann/Portfolio/Components/PortfolioSections/BasicInfo";
import { RelatedSection } from "web_myplann/Portfolio/Components/RelatedSection";
import { computeRelatedPresence } from "web_myplann/Portfolio/Helpers/getHasRelated";
import {
	DataCard,
	DataCardPreset,
} from "web_myplann/Shared/Components/DataCard";
import { DataRow } from "web_myplann/Shared/Components/DataRow";
import { moneyFormatter } from "web_myplann/Shared/Utils/formatters";
import type { PortfolioDetailProps } from "web_myplann/Utils/types";

const PageResources = Resources.PortfolioDetail.NonLifeInsurance;

export const PropertyInsurance: FC<PortfolioDetailProps> = ({
	baseData,
	currencySign,
}) => {
	const { t } = useResource();

	const { data: documents, isLoading: isDocumentsLoading } =
		usePortfolioDocumentsQuery(baseData.id);

	if (isDocumentsLoading) {
		return <DetailSectionLoader />;
	}

	const portfolioData = baseData.data as PropertyInsuranceDetailDataDTO;

	return (
		<Stack>
			<DataCard
				preset={DataCardPreset.BasicInfo}
				bottomComponent={<BasicInfo data={baseData} />}
				noDivider={
					!computeRelatedPresence(documents?.data, baseData.related).hasAny
				}
			>
				<DataRow label={t(PageResources.name)} value={baseData.clientName} />
				<DataRow
					label={t(PageResources.contractNumber)}
					value={baseData?.contractNumber}
				/>
				<DataRow
					label={t(PageResources.producent)}
					value={baseData?.producer}
				/>
				<DataRow label={t(PageResources.product)} value={baseData?.product} />
				<DataRow
					label={t(PageResources.insured)}
					value={moneyFormatter(portfolioData?.insurance, currencySign)}
				/>
				<DataRow
					label={t(PageResources.insurancePeriod)}
					value={moneyFormatter(portfolioData?.insurancePeriod, currencySign)}
				/>
			</DataCard>

			<RelatedSection
				documents={documents?.data}
				related={baseData.related}
				portfolioId={baseData.id}
			/>
		</Stack>
	);
};
